/**
 * The `secret` control kind — README §6: "secret (masked, with Replace →
 * paste → Save, and never rendering a value back)". Task-18 requirement #2:
 * "A secret is write-only... not even partially — because this page is
 * shoulder-surfable in an office."
 *
 * This component never reads the field's stored `value` at all — not on
 * mount, not after a save. The only text it ever shows is a locally-owned
 * status string, seeded from the field's own masked `default` (e.g.
 * "set · 12 Jul 2026", itself already just status copy, never the secret)
 * and updated to a generic "replaced" status after a successful save. The
 * pasted draft lives only in this component's own state and is discarded
 * the moment Save resolves (or the edit is cancelled) — it is never lifted
 * into the shared store, which would let it leak into e.g. a Redux
 * devtools panel or a future debug dump.
 */
import { useState } from 'react'
import type { ConfigField } from '@communicator/shared'
import { Button, Chip } from '../ui'

export interface SecretFieldProps {
  field: ConfigField
  locked: boolean
  onCommit: (id: string, value: unknown) => Promise<void>
}

export function SecretField({ field, locked, onCommit }: SecretFieldProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState<string>(typeof field.default === 'string' ? field.default : 'set')
  const [error, setError] = useState<string | null>(null)

  async function save() {
    if (draft.trim() === '') return
    setBusy(true)
    setError(null)
    try {
      await onCommit(field.id, draft)
      // Draft is cleared only on the success path, deliberately: it holds
      // the plaintext secret, and once `onCommit` resolves that plaintext
      // has done its one job (the request that carried it) and must not
      // linger in this component's state either. On failure it stays so a
      // retry doesn't force retyping — `onCommit` itself (SettingsPage's
      // commitField) never lifts a secret's value into the shared store on
      // any path, success or failure.
      setStatus('Set · replaced')
      setDraft('')
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.')
    } finally {
      setBusy(false)
    }
  }

  function cancel() {
    setDraft('')
    setEditing(false)
    setError(null)
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-2" data-testid={`secret-${field.id}`}>
        <Chip tone="neutral">{status}</Chip>
        <Button variant="secondary" disabled={locked} onClick={() => setEditing(true)}>
          Replace
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-end gap-1" data-testid={`secret-${field.id}-edit`}>
      <div className="flex items-center gap-2">
        <input
          type="password"
          value={draft}
          autoComplete="off"
          placeholder="Paste new value"
          aria-label={`${field.label} — new value`}
          onChange={(e) => setDraft(e.target.value)}
          className="h-[34px] w-[220px] rounded-[8px] border border-subtle bg-card px-2.5 text-[13px] text-hi"
        />
        <Button variant="primary" disabled={busy || draft.trim() === ''} onClick={() => void save()}>
          Save
        </Button>
        <Button variant="secondary" onClick={cancel}>
          Cancel
        </Button>
      </div>
      {error ? <p className="text-[12px] text-bad">{error}</p> : null}
    </div>
  )
}
